import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, FileText, Warehouse, Trash2, Search, CheckCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const WarehouseDashboard = () => {
  const [quotations, setQuotations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quotations');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [quotationsRes, invoicesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/warehouse/quotations`),
        axios.get(`${BACKEND_URL}/api/warehouse/invoices`)
      ]);
      setQuotations(quotationsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConfirmQuotation = async (quotationId) => {
    if (!window.confirm('Confirm this quotation? This will generate an invoice.')) return;
    try {
      const res = await axios.patch(`${BACKEND_URL}/api/warehouse/quotations/${quotationId}/confirm`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to confirm quotation');
    }
  };

  const handleDeleteQuotation = async (quotationId) => {
    if (!window.confirm('Are you sure you want to delete this quotation?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/warehouse/quotations/${quotationId}`);
      toast.success('Quotation deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete quotation');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/warehouse/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/warehouse/invoices/${invoiceId}/status`, { status: 'paid' });
      toast.success('Invoice marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-SG');
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      unpaid: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || styles.draft}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  const filteredQuotations = quotations.filter(q => 
    q.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.quotation_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredInvoices = invoices.filter(inv => 
    inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-44 px-4 flex justify-center items-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-44 pb-12 px-4 bg-gray-50">
      <div className="container mx-auto max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Warehouse className="text-blue-600" />
              Warehouse Billing
            </h1>
            <p className="text-gray-600">Manage warehouse storage quotations and invoices</p>
          </div>
          <div className="flex gap-4">
            <Link to="/admin/warehouse/create-quotation">
              <Button>
                <Plus size={16} className="mr-2" />
                New Quotation
              </Button>
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <Button 
            variant={activeTab === 'quotations' ? 'default' : 'outline'}
            onClick={() => setActiveTab('quotations')}
          >
            <FileText size={16} className="mr-2" />
            Quotations ({quotations.length})
          </Button>
          <Button 
            variant={activeTab === 'invoices' ? 'default' : 'outline'}
            onClick={() => setActiveTab('invoices')}
          >
            <FileText size={16} className="mr-2" />
            Invoices ({invoices.length})
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search by customer, company, or number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Quotations Tab */}
        {activeTab === 'quotations' && (
          <div className="space-y-4">
            {filteredQuotations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  No warehouse quotations found. Create your first quotation.
                </CardContent>
              </Card>
            ) : (
              filteredQuotations.map(quotation => (
                <Card key={quotation.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-semibold text-lg">{quotation.quotation_number}</p>
                          {getStatusBadge(quotation.status)}
                        </div>
                        <p className="text-gray-800">{quotation.company_name || quotation.customer_name}</p>
                        <p className="text-sm text-gray-500">
                          Date: {formatDate(quotation.quotation_date)} | Valid until: {formatDate(quotation.valid_until)}
                        </p>
                      </div>
                      <div className="text-right mr-6">
                        <p className="text-2xl font-bold text-blue-600">{formatCurrency(quotation.total_amount)}</p>
                        <p className="text-sm text-gray-500">Total Amount</p>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/admin/warehouse/quotation/${quotation.id}`}>
                          <Button variant="outline" size="sm">
                            <Eye size={16} className="mr-1" />
                            View
                          </Button>
                        </Link>
                        {quotation.status === 'draft' && (
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleConfirmQuotation(quotation.id)}
                          >
                            <CheckCircle size={16} className="mr-1" />
                            Confirm
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteQuotation(quotation.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-4">
            {filteredInvoices.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  No warehouse invoices found.
                </CardContent>
              </Card>
            ) : (
              filteredInvoices.map(invoice => (
                <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-semibold text-lg">{invoice.invoice_number}</p>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <p className="text-gray-800">{invoice.company_name || invoice.customer_name}</p>
                        <p className="text-sm text-gray-500">
                          Date: {formatDate(invoice.invoice_date)} | Due: {formatDate(invoice.due_date)}
                        </p>
                      </div>
                      <div className="text-right mr-6">
                        <p className="text-2xl font-bold text-green-600">{formatCurrency(invoice.total_amount)}</p>
                        <p className="text-sm text-gray-500">Amount Due</p>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/admin/warehouse/invoice/${invoice.id}`}>
                          <Button variant="outline" size="sm">
                            <Eye size={16} className="mr-1" />
                            View
                          </Button>
                        </Link>
                        {invoice.status === 'unpaid' && (
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleMarkPaid(invoice.id)}
                          >
                            <CheckCircle size={16} className="mr-1" />
                            Mark Paid
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteInvoice(invoice.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WarehouseDashboard;
